/**
 * @file components/log/WeightLogModal.jsx
 * @description Refined modal for weight tracking with enhanced dark-mode aesthetics.
 */

import { useState } from "react";
import { X, Scale, Calendar, Pencil, Loader2 } from "lucide-react";

export default function WeightLogModal({ isOpen, onClose, onSave, loading }) {
  const [weight, setWeight] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const WEIGHT_MIN = 20;
  const WEIGHT_MAX = 350;

  const handleSave = async () => {
    setError("");
    const w = parseFloat(weight);
    if (!weight || isNaN(w) || w <= 0) {
      setError("Please enter a valid weight");
      return;
    }
    if (w < WEIGHT_MIN || w > WEIGHT_MAX) {
      setError(`Weight must be between ${WEIGHT_MIN}–${WEIGHT_MAX} kg`);
      return;
    }
    const selected = new Date(date);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (selected > today) {
      setError("Date cannot be in the future");
      return;
    }

    try {
      await onSave(w, selected, notes);
      setWeight("");
      setNotes("");
      setDate(new Date().toISOString().split("T")[0]);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save weight");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Heavy Backdrop Blur */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative bg-[#0F1115] border border-white/10 border-b-0 sm:border-b w-full sm:max-w-sm rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 pt-6 pb-2 flex justify-between items-start">
          <div>
            <h2 className="font-serif text-xl text-white mb-0.5">Log Weight</h2>
            <p className="text-[9px] uppercase tracking-widest text-gray-600 font-bold">
              Morning Tracking
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-full text-gray-500 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-8 pt-3 space-y-4">
          {/* Weight Input */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1">
              <Scale size={11} className="text-brand-green" /> Weight (kg)
            </label>
            <input
              type="number"
              step="0.1"
              min={WEIGHT_MIN}
              max={WEIGHT_MAX}
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="e.g. 93.0"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:border-brand-green/40 focus:bg-white/[0.06] outline-none transition-all text-lg font-bold"
            />
            <p className="text-[9px] text-gray-600 ml-1 mt-1">{WEIGHT_MIN}–{WEIGHT_MAX} kg</p>
          </div>

          {/* Date Input */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1">
              <Calendar size={11} /> Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full min-w-0 max-w-full appearance-none bg-white/[0.04] border border-white/10 rounded-xl px-3 py-3.5 text-white outline-none focus:border-brand-green/40 focus:bg-white/[0.06] transition-all text-sm font-medium [color-scheme:dark]"
            />
          </div>

          {/* Notes Input */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1">
              <Pencil size={11} /> Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How are you feeling?"
              rows="2"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 outline-none focus:border-brand-green/40 focus:bg-white/[0.06] transition-all resize-none text-sm"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs animate-in fade-in slide-in-from-top-1">
              {error}
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleSave}
            disabled={loading || !weight}
            className="w-full flex items-center justify-center gap-3 py-4 mt-1 rounded-xl 
                       bg-brand-green text-[#0F1115] font-bold text-xs uppercase tracking-widest 
                       hover:bg-green-400 transition-all active:scale-[0.98] disabled:opacity-20 
                       shadow-[0_0_20px_rgba(16,185,129,0.15)]"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              "Save Measurement"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
